import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Two-state landing page: enter a new password → confirmation.
// Token comes from `?token=...` in the URL. If missing/expired/used, the
// backend returns 400 and we render an error with a "Request a new link"
// path back to /sign-in.
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  useSeo({ title: "Reset password — Bilateral Calm", path: "/reset-password", noindex: true });

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    // Always recompute validation from scratch — never let a stale error
    // from a previous submit linger when the user fixes the input.
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
    } catch (err) {
      const code = err.response?.data?.detail;
      if (code === "token_expired") {
        setError("This reset link has expired. Request a fresh one.");
      } else if (code === "invalid_or_used_token") {
        setError("This reset link is invalid or has already been used.");
      } else if (code === "invalid_account") {
        setError("This account doesn't support password reset.");
      } else {
        setError("Couldn't reset your password. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FBF8F1] flex flex-col"
      data-testid="reset-password-page"
    >
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-6 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl border border-slate-100 shadow-[0_14px_44px_rgba(0,0,0,0.06)] p-8 sm:p-10">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#E7F4EA] flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#3FA363]" />
              </div>
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                Password updated
              </h1>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                You're all set. Sign in with your new password to continue.
              </p>
              <Button
                onClick={() => navigate("/sign-in")}
                className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="reset-go-signin-btn"
              >
                Go to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="w-12 h-12 rounded-full bg-[#E7F0F4] flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#3090A8]" />
              </div>
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                Choose a new password
              </h1>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Pick something you haven't used before. We'll sign you out of
                every device so the new password is required everywhere.
              </p>

              <label htmlFor="reset-password-input" className="block text-sm font-medium text-[#1B2349] mb-1.5">
                New password
              </label>
              <div className="relative mb-3">
                <Input
                  id="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  className="rounded-xl pr-11"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-[#718096] hover:text-[#1B2349] transition-colors"
                  data-testid="reset-show-password"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <label htmlFor="reset-confirm-input" className="block text-sm font-medium text-[#1B2349] mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <Input
                  id="reset-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-type your new password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  className="rounded-xl pr-11"
                  data-testid="reset-confirm-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-[#718096] hover:text-[#1B2349] transition-colors"
                  data-testid="reset-show-confirm"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {error && (
                <div className="mt-4 flex items-start gap-2 text-sm text-[#D98880]" data-testid="reset-error">
                  <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={busy || !token}
                className="mt-6 w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="reset-submit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>

              <button
                type="button"
                onClick={() => navigate("/sign-in")}
                className="mt-4 w-full text-center text-sm text-[#3090A8] hover:text-[#1B2349]"
                data-testid="reset-back-btn"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default ResetPasswordPage;
